"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { useSession } from "@/lib/client/auth/client";
import { CountrySelector } from "@/lib/client/components/CountrySelector";
import { useLoadingStore } from "@/lib/client/stores/useLoadingStore";
import { fetcher } from "@/lib/http/fetcher";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from "@/ui";

// define form schema
export const formSchema = z.object({
  address: z.string().min(1).max(50),
  address2: z.string().optional(),
  city: z.string().min(1).max(50),
  country: z.string().min(1).max(50),
  region: z.string().optional(),
  postalCode: z.string().min(1).max(50),
});

export const AddressForm = () => {
  const generalT = useTranslations("general");
  const validationT = useTranslations("validation");
  const checkoutT = useTranslations("checkout");
  const profileT = useTranslations("profile.address");
  const { data: session } = useSession();
  const sessionUser = session?.user as
    | { id: string; address?: Record<string, unknown> | null }
    | undefined;
  const { setIsLoading } = useLoadingStore();

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (
      sessionUser?.address &&
      typeof sessionUser.address === "object" &&
      !Array.isArray(sessionUser.address)
    ) {
      const address = sessionUser.address as Record<string, unknown>;
      reset({
        address: (address.address as string) || "",
        address2: (address.address2 as string) || "",
        city: (address.city as string) || "",
        country: (address.country as string) || "",
        region: (address.region as string) || "",
        postalCode: (address.postalCode as string) || "",
      });
    }
  }, [sessionUser?.address, reset]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);

    try {
      const userData = {
        address: data,
      };
      await fetcher("/api/users/me", {
        method: "PATCH",
        body: JSON.stringify(userData),
      });
      toast.success(profileT("update_success") || "Address updated successfully");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update address");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="">
        <CardHeader>
          <CardTitle>{profileT("title")}</CardTitle>
          <CardDescription>{profileT("description")}</CardDescription>
        </CardHeader>
        <Form {...form}>
          <form
            className="grid grid-cols-2 gap-2 lg:gap-4"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Shipping information */}
            <FormField
              control={control}
              name="address"
              render={({ field }) => (
                <FormItem className={"col-span-2"}>
                  <FormControl>
                    <Input
                      placeholder={checkoutT("address")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage>
                    {errors.address && validationT("required", { field: "Address" })}
                  </FormMessage>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="address2"
              render={({ field }) => (
                <FormItem className={"col-span-2"}>
                  <FormControl>
                    <Input
                      placeholder={checkoutT("apartment")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage>
                    {errors.address2 && validationT("required", { field: "Apartment" })}
                  </FormMessage>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="city"
              render={({ field }) => (
                <FormItem className={""}>
                  <FormControl>
                    <Input
                      placeholder={checkoutT("city")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage>
                    {errors.city && validationT("required", { field: "City" })}
                  </FormMessage>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="country"
              render={({ field }) => (
                <FormItem className={""}>
                  <FormControl>
                    <CountrySelector field={field} />
                  </FormControl>
                  <FormMessage>
                    {/*{errors.country &&*/}
                    {/*  validationT("required", { field: "Country" })}*/}
                  </FormMessage>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="region"
              render={({ field }) => (
                <FormItem className={""}>
                  <FormControl>
                    <Input
                      placeholder={checkoutT("state")}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="postalCode"
              render={({ field }) => (
                <FormItem className={""}>
                  <FormControl>
                    <Input
                      placeholder={checkoutT("postal_code")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage>
                    {errors.postalCode && validationT("required", { field: "Postal code" })}
                  </FormMessage>
                </FormItem>
              )}
            />
            <CardFooter className={"col-span-2 p-0"}>
              <Button
                type="submit"
                className={"w-full"}
                disabled={!(sessionUser && form.formState.isDirty)}
              >
                {generalT("submit")}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
